import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useParams,
    useLocation,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import './App.css';
import Login from './Login.js';
import NavbarLayout from "./NavbarLayout.js";
import Sidebar from "./Sidebar.js";
import ProductBOM from "./ProductBOM.js";
import DynamicForm from "./DynamicForm.js";
import DynamicSearch from "./DynamicSearch.js";
import DynamicDashboard from "./DynamicDashboard.js";
import Quote from "./Quote.js";
import QuoteCreate from "./QuoteCreate.js";
import QuoteEdit from "./QuoteEdit.js";
import MspQuote from "./MspQuote.js";
import QuoteReviewQueue from "./QuoteReviewQueue.js";
import QuoteReviewDetail from "./QuoteReviewDetail.js";
import Order from "./Order.js";
import Shipment from "./Shipment.js";
import ShipmentView from "./ShipmentView.js";
import MyShipments from "./MyShipments.js";
import AssignShipments from "./AssignShipments.js";
import OrderView from "./OrderView.js";
import ShipmentEntry from "./ShipmentEntry.js";
import ARDashboard from "./invoices/ARDashboard.js";
import InvoiceDetail from "./invoices/InvoiceDetail.js";
import FinanceCockpit from "./finance/FinanceCockpit.js";
import BatchDetail from "./finance/BatchDetail.js";
import FinanceInvoiceDetail from "./finance/FinanceInvoiceDetail.js";
import Customer from "./Customer.js";
import PriceContracts from "./PriceContracts.js";
import RoleAccess from "./RoleAccess.js";
import RequireAuth from "./components/RequireAuth.js";
import RequireAction from "./components/RequireAction.js";
import NotFound from "./components/NotFound.js";
import { installAuthFetchInterceptor, isAuthenticated, clearSessionIfExpired, isShuklaIt } from "./utils/auth";
import { prefetchForSession } from "./utils/refDataCache";
import { prefetchSearchResults } from "./utils/searchCache";

// A session cookie can survive a browser restart ("continue where you left
// off"), so we may boot holding a token that's already past its server-side
// expiry. Wipe it before anything renders — otherwise token-presence checks
// (e.g. the navbar's isLoggedIn) show stale logged-in chrome until the first
// API call bounces the user.
clearSessionIfExpired();

// Install once at app boot — any 401 from our API on any fetch call (including
// the ones not yet migrated to apiFetch) will clear session + bounce to /login.
installAuthFetchInterceptor();

// Customer module → the bespoke step-by-step Customer page; every other module
// → the generic DynamicSearch / DynamicForm. Slug-agnostic (customer /
// customers / customers-2 …).
const isCustomerSlug = (s) => (s || "").toLowerCase().startsWith("customer");
function ModuleSearch() {
    const { moduleSlug } = useParams();
    return isCustomerSlug(moduleSlug) ? <Customer /> : <DynamicSearch />;
}
function ModuleCreate() {
    const { moduleSlug } = useParams();
    return isCustomerSlug(moduleSlug) ? <Customer initialView="create" /> : <DynamicForm />;
}
// The edit form gets its record from the search row (route state). A typed or
// bookmarked /:slug/edit has no record, so go back to the list instead of
// opening an empty form that would save as a new record (UI-CUS-091).
function ModuleEdit() {
    const { moduleSlug } = useParams();
    const { state } = useLocation();
    return state?.editData ? <DynamicForm /> : <Navigate to={`/${moduleSlug}/search`} replace />;
}

// Re-warm the searchable-dropdown reference lists and the list pages' default
// first-page results after a page reload (the in-memory caches don't survive a
// hard refresh, but the JWT in sessionStorage does). No-op when not signed in.
// prefetchSearchResults delays itself so the landing page's calls go first.
if (isAuthenticated()) {
  prefetchForSession();
  prefetchSearchResults();
}

const root = ReactDOM.createRoot(document.getElementById('root'));
// StrictMode is intentionally omitted: in development it double-invokes effects,
// which fired every page-load data fetch twice (Quote / Menu / Customer …).
// (StrictMode only affects dev; production already ran effects once.)
root.render(
    <div className="App">
      <Router>
                <Routes>
                    <Route
                        path="/"
                        element={<Navigate to="/home" replace />}
                    />

                    {/* Public routes (shared navbar) — home + product BOM are
                        accessible without auth so logout/back-to-home and
                        deep-linked product pages still render for anonymous
                        visitors. ProductBOM gates its sensitive actions on its
                        own using sessionStorage.

                        /home (bare) renders the landing page. /home/* accepts
                        BOTH a hierarchical slug path
                        ("/home/products/shukla-hip/blade") and the legacy
                        numeric menuId ("/home/12") via the wildcard splat —
                        Sidebar resolves whichever form it gets. */}
                    <Route element={<NavbarLayout />}>
                        <Route
                            path="/home/*"
                            element={<Sidebar />}
                        />
                        <Route
                            path="/home"
                            element={<Sidebar />}
                        />
                        <Route
                            path="/product-bom/:partNum"
                            element={<ProductBOM />}
                        />
                    </Route>

                    {/* Protected routes (shared navbar) — anything that hits
                        an authenticated API. RequireAuth bounces to /login
                        with ?returnTo set when the token is missing/expired.

                        Module-action URL grammar:
                          /:moduleSlug/search        list/search page
                          /:moduleSlug/create        new-record form
                          /:moduleSlug/edit          edit form (record via state)
                        e.g. /customer/search, /sales-representatives/create.
                        :moduleSlug accepts either a numeric menuId (legacy)
                        or the slug. Component resolves whichever form arrived.

                        These dynamic patterns are listed AFTER the literal
                        /dashboard, /quote and /home routes so React Router's
                        ranking picks the more-specific literal matches first
                        (otherwise /quote/edit would route here). */}
                    <Route element={<RequireAuth><NavbarLayout /></RequireAuth>}>
                        {/* Customer module gets the bespoke step-by-step page;
                            all other modules fall through to the generic
                            DynamicSearch / DynamicForm. */}
                        <Route
                            path="/:moduleSlug/search"
                            element={<ModuleSearch />}
                        />
                        <Route
                            path="/:moduleSlug/create"
                            element={<ModuleCreate />}
                        />
                        <Route
                            path="/:moduleSlug/edit"
                            element={<ModuleEdit />}
                        />
                        <Route
                            path="/:moduleSlug/edit/:recordId"
                            element={<ModuleEdit />}
                        />
                        {/* DB-driven dashboards. /dashboard (no slug) is the
                            canonical URL — DynamicDashboard defaults to the
                            "sales-rep" config when dashboardKey is absent.
                            The keyed routes remain so future second-dashboard
                            configs (e.g. /dashboard/admin) still work. */}
                        {/* Route-level entitlement guards (RequireAction):
                            each module route requires its search/view action
                            — same ids the navbar menu filtering uses via
                            Menu.RequiredActionId. Covers direct-URL access. */}
                        <Route
                            path="/dashboard"
                            element={<RequireAction action="00054"><DynamicDashboard /></RequireAction>}
                        />
                        <Route
                            path="/dashboard/:dashboardKey"
                            element={<RequireAction action="00054"><DynamicDashboard /></RequireAction>}
                        />
                        <Route
                            path="/dashboard/:dashboardKey/:routeParam"
                            element={<RequireAction action="00054"><DynamicDashboard /></RequireAction>}
                        />
                        {/* Single Quote menu lands here — search + Add button +
                            row→edit. DB menu row 2101 (renamed "Quotes") */}
                        <Route
                            path="/quote"
                            element={<RequireAction action="00031"><Quote /></RequireAction>}
                        />
                        {/* Sales Quote builder — reached via "+ Add Quote" or
                            direct deep link. */}
                        <Route
                            path="/quote/new"
                            element={<RequireAction action="00036"><QuoteCreate /></RequireAction>}
                        />
                        {/* Edit existing quote. /quote/edit shows a lookup,
                            /quote/edit/:quoteNum auto-loads (deep link from
                            the row click on /quote). Guarded by VIEW (00032) —
                            the in-page Edit/Save buttons need 00037. */}
                        <Route
                            path="/quote/edit"
                            element={<RequireAction action="00032"><QuoteEdit /></RequireAction>}
                        />
                        <Route
                            path="/quote/edit/:quoteNum"
                            element={<RequireAction action="00032"><QuoteEdit /></RequireAction>}
                        />
                        {/* Multi-shipment board — per-shipment statuses, PO
                            upload + coverage, claim/release and partial
                            conversion for an MSP quote. Guarded by VIEW
                            (00032); upload/claim/convert buttons gate
                            themselves on 00040/00041/00039 in-page. */}
                        <Route
                            path="/quote/msp/:quoteNum"
                            element={<RequireAction action="00032"><MspQuote /></RequireAction>}
                        />
                        {/* Role Access matrix — IT only (navbar icon is
                            gated the same way; the API re-checks server-side). */}
                        <Route
                            path="/role-access"
                            element={<RequireAction allow={isShuklaIt}><RoleAccess /></RequireAction>}
                        />
                        {/* Customer-Service conversion review (maker-checker).
                            Queue + per-quote tally/approve. Role-gated inside
                            the components (CustomerService / admin = approver;
                            a rep sees only their own, read-only). */}
                        <Route
                            path="/reviews"
                            element={<RequireAction action="00044"><QuoteReviewQueue /></RequireAction>}
                        />
                        <Route
                            path="/reviews/:quoteNum"
                            element={<RequireAction action="00044"><QuoteReviewDetail /></RequireAction>}
                        />
                        {/* Single Orders menu lands here — sales-order search +
                            row→view. DB menu row RoutePath='/orders'
                            (SQL/024_AddOrdersMenuItem.sql). Listed before the
                            /order/:orderNum literal so it isn't shadowed. */}
                        <Route
                            path="/orders"
                            element={<RequireAction action="00048"><Order /></RequireAction>}
                        />
                        {/* Read-only Sales Order view — reached from the Orders
                            search, the conversion success screen, or review
                            detail once an order exists. Deep-linkable by number. */}
                        <Route
                            path="/order/:orderNum"
                            element={<RequireAction action="00049"><OrderView /></RequireAction>}
                        />
                        {/* Generate & Ship wizard — FedEx label + Epicor pack
                            + pack slip, launched from the OrderView "Ship this
                            order" button. Mirrors shipment-flow-simulator.html. */}
                        <Route
                            path="/order/:orderNum/ship"
                            element={<RequireAction action="00067"><ShipmentEntry /></RequireAction>}
                        />
                        {/* Shipping history — pack search + row→shipment view.
                            Mirrors /orders. DB menu row RoutePath='/shipments'
                            (SQL/025_AddShipmentsMenuItem.sql). */}
                        <Route
                            path="/shipments"
                            element={<RequireAction action="00051"><Shipment /></RequireAction>}
                        />
                        {/* Read-only shipment (pack) view — reached from the
                            Shipping search rows and the quote/order expansion
                            pack chips. Deep-linkable by pack number. */}
                        <Route
                            path="/shipment/:packNum"
                            element={<RequireAction action="00052"><ShipmentView /></RequireAction>}
                        />
                        {/* Dispatcher work queue (SQL/048). My Shipments is the
                            warehouse dispatcher's single screen (role ShpRec);
                            Assign Shipments is the admin daily-Excel upload +
                            reassignment. DB menu rows carry the same actions. */}
                        <Route
                            path="/my-shipments"
                            element={<RequireAction action="00080"><MyShipments /></RequireAction>}
                        />
                        <Route
                            path="/assign-shipments"
                            element={<RequireAction action="00081"><AssignShipments /></RequireAction>}
                        />
                        {/* AR / Invoices dashboard (Phase 2 — ar-due port).
                            List (register + due buckets + reminders) and the
                            one-call invoice detail. DB menu row RoutePath=
                            '/invoices' (SQL/041), gate action 00068. */}
                        <Route
                            path="/invoices"
                            element={<RequireAction action="00068"><ARDashboard /></RequireAction>}
                        />
                        <Route
                            path="/invoices/:invoiceNum"
                            element={<RequireAction action="00068"><InvoiceDetail /></RequireAction>}
                        />
                        {/* Finance cockpit (Phase 3 — Invoice Program port).
                            Batches / posting queue / unbilled + batch review +
                            finance invoice detail. DB menu row RoutePath=
                            '/finance' (SQL/042), gate action 00074. */}
                        <Route
                            path="/finance"
                            element={<RequireAction action="00074"><FinanceCockpit /></RequireAction>}
                        />
                        <Route
                            path="/finance/batch/:batchCode"
                            element={<RequireAction action="00074"><BatchDetail /></RequireAction>}
                        />
                        <Route
                            path="/finance/invoice/:invoiceNum"
                            element={<RequireAction action="00074"><FinanceInvoiceDetail /></RequireAction>}
                        />
                        {/* Contract pricing admin — list / detail / lifecycle in
                            one page. Gate action 00082 = view (SQL/056); the
                            in-page buttons gate themselves on 00083 (manage)
                            and 00084 (approve/enable/disable). No DB menu row
                            exists yet, so this is reachable by URL only. */}
                        <Route
                            path="/price-contracts"
                            element={<RequireAction action="00082"><PriceContracts /></RequireAction>}
                        />
                    </Route>

                    {/* Login without navbar */}
                    <Route
                        path="/login"
                        element={<Login />}
                    />

                    {/* UI-NAV-042: any other address — a not-found card with the
                        navbar, instead of a blank page. */}
                    <Route element={<NavbarLayout />}>
                        <Route path="*" element={<NotFound />} />
                    </Route>

                </Routes>
            </Router>
    </div>
);

reportWebVitals();
